// pages/Dashboard/Appointments.js
import { useState, useMemo, useEffect, useCallback } from 'react';
import {
  FaCalendarAlt,
  FaClock,
  FaUserPlus,
  FaChevronLeft,
  FaChevronRight,
  FaUserCircle,
  FaMobileAlt,
  FaCut,
  FaCheckCircle,
  FaHourglassHalf,
  FaWalking,
  FaTimes,
  FaListUl,
  FaThLarge,
  FaReceipt,
} from 'react-icons/fa';
import '../Css/Appointments.css';

import { useToast } from '../../components/Toast';
import { baseurl } from '../Baseurl';
import { useNavigate } from 'react-router-dom';



const TIME_SLOTS = [
  '09:00', '09:30', '10:00', '10:30', '11:00', '11:30',
  '12:00', '12:30', '13:00', '13:30', '14:00', '14:30',
  '15:00', '15:30', '16:00', '16:30', '17:00', '17:30',
  '18:00', '18:30', '19:00',
];

// Helper to convert 24hr "HH:mm" to 12hr format like "9:00 AM"
function to12Hour(time24) {
  const [h, m] = time24.split(':').map(Number);
  const period = h >= 12 ? 'PM' : 'AM';
  const hour12 = h % 12 === 0 ? 12 : h % 12;
  return `${hour12}:${String(m).padStart(2, '0')} ${period}`;
}

const STATUS_STYLES = {
  upcoming: { label: 'Upcoming', icon: <FaHourglassHalf />, cls: 'status-upcoming' },
  progress: { label: 'In Progress', icon: <FaClock />, cls: 'status-progress' },
  completed: { label: 'Completed', icon: <FaCheckCircle />, cls: 'status-completed' },
  walkin: { label: 'Walk-in', icon: <FaWalking />, cls: 'status-walkin' },
  cancelled: { label: 'Cancelled', icon: <FaTimes />, cls: 'status-cancelled' },
};

// Add a helper to round any time to the nearest available TIME_SLOT
function getNearestSlot(timeStr) {
  const [h, m] = timeStr.split(':').map(Number);
  const totalMinutes = h * 60 + m;

  let nearest = TIME_SLOTS[0];
  let smallestDiff = Infinity;

  TIME_SLOTS.forEach((slot) => {
    const [sh, sm] = slot.split(':').map(Number);
    const slotMinutes = sh * 60 + sm;
    const diff = Math.abs(slotMinutes - totalMinutes);
    if (diff < smallestDiff) {
      smallestDiff = diff;
      nearest = slot;
    }
  });

  return nearest;
}


// Add a helper to get current time in HH:mm format
function getCurrentTime() {
  const now = new Date();
  const h = String(now.getHours()).padStart(2, '0');
  const m = String(now.getMinutes()).padStart(2, '0');
  return `${h}:${m}`;
}


function formatDate(date) {
  return date.toLocaleDateString('en-IN', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });
}

function toApiDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

const EMPTY_FORM = (staffList, servicesList) => ({
  client: '',
  phone: '',
  serviceId: servicesList[0]?.id || '',
  staffId: staffList[0]?.id || '',
  time: TIME_SLOTS[0],
});

function Appointments() {
  const [currentDate, setCurrentDate] = useState(new Date());
  const [view, setView] = useState('schedule'); // schedule | list

  const [staffList, setStaffList] = useState([]);
  const [servicesList, setServicesList] = useState([]);
  const [appointments, setAppointments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [showBookingForm, setShowBookingForm] = useState(false);
  const [formType, setFormType] = useState('booking'); // booking | walkin
  const [form, setForm] = useState({ client: '', phone: '', serviceId: '', staffId: '', time: TIME_SLOTS[0] });
  const [saving, setSaving] = useState(false);

  const toast = useToast();

  const navigate = useNavigate();



  const authHeaders = () => ({
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('accessToken')}`,
  });


// Add handler to get encrypted appointment id from backend and go to billing
  const goToBilling = async (appointmentId) => {
  try {
    const res = await fetch(`${baseurl}/appointments/${appointmentId}/billing-token`, {
      headers: authHeaders(),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to open billing');

    navigate(`/dashboard/billing?rule_page=appointment&ap_id=${data.data.token}`);
  } catch (err) {
    toast(err.message || 'Failed to open billing', 'error');
  }
};



  /* -------------------- FETCH STAFF & SERVICES (once) -------------------- */
  const fetchStaffAndServices = useCallback(async () => {
    try {
      const [staffRes, serviceRes] = await Promise.all([
        fetch(`${baseurl}/staff?status=active`, { headers: authHeaders() }),
        fetch(`${baseurl}/services?status=active`, { headers: authHeaders() }),
      ]);

      const staffData = await staffRes.json();
      const serviceData = await serviceRes.json();

      if (!staffRes.ok) throw new Error(staffData.message || 'Failed to fetch staff');
      if (!serviceRes.ok) throw new Error(serviceData.message || 'Failed to fetch services');

      setStaffList(staffData.data.staff);
      setServicesList(serviceData.data.services);
    } catch (err) {
      setError(err.message);
      toast(err.message || 'Failed to load staff/services', 'error');
    }
  }, []);

  /* -------------------- FETCH APPOINTMENTS FOR SELECTED DATE -------------------- */
  const fetchAppointments = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const dateStr = toApiDate(currentDate);
      const res = await fetch(`${baseurl}/appointments?date=${dateStr}`, {
        headers: authHeaders(),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to fetch appointments');

      setAppointments(data.data.appointments);
    } catch (err) {
      setError(err.message);
      toast(err.message || 'Failed to fetch appointments', 'error');
    } finally {
      setLoading(false);
    }
  }, [currentDate]);

  useEffect(() => {
    fetchStaffAndServices();
  }, [fetchStaffAndServices]);

  useEffect(() => {
    fetchAppointments();
  }, [fetchAppointments]);

  const changeDay = (delta) => {
    const next = new Date(currentDate);
    next.setDate(next.getDate() + delta);
    setCurrentDate(next);
  };

const openForm = (type) => {
  setFormType(type);
  setForm({
    ...EMPTY_FORM(staffList, servicesList),
    time: type === 'walkin' ? getCurrentTime() : TIME_SLOTS[0],
  });
  setShowBookingForm(true);
};


const handleSubmit = async (e) => {
  e.preventDefault();

  if (!form.client.trim()) {
    toast('Please enter client name', 'error');
    return;
  }
  if (!form.serviceId || !form.staffId) {
    toast('Please select a service and staff member', 'error');
    return;
  }

  setSaving(true);
  try {
    const payload = {
      clientName: form.client,
      clientPhone: form.phone,
      serviceId: Number(form.serviceId),
      staffId: Number(form.staffId),
      appointmentDate: toApiDate(currentDate),
      appointmentTime: formType === 'walkin' ? getCurrentTime() : form.time,
      status: formType === 'walkin' ? 'walkin' : 'upcoming',
    };

    const res = await fetch(`${baseurl}/appointments`, {
      method: 'POST',
      headers: authHeaders(),
      body: JSON.stringify(payload),
    });

    const data = await res.json();
    if (!res.ok) throw new Error(data.message || 'Failed to book appointment');

    toast(data.message || 'Appointment booked successfully', 'success');
    setShowBookingForm(false);
    await fetchAppointments();
  } catch (err) {
    toast(err.message || 'Failed to book appointment', 'error');
  } finally {
    setSaving(false);
  }
};

  const updateStatus = async (id, status) => {
    try {
      const res = await fetch(`${baseurl}/appointments/${id}/status`, {
        method: 'PATCH',
        headers: authHeaders(),
        body: JSON.stringify({ status }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || 'Failed to update status');

      toast(data.message || 'Appointment status updated', 'success');
      await fetchAppointments();
    } catch (err) {
      toast(err.message || 'Failed to update status', 'error');
    }
  };

  const sortedAppointments = useMemo(
    () => [...appointments].sort((a, b) => a.appointment_time.localeCompare(b.appointment_time)),
    [appointments]
  );

const appointmentsByStaffAndTime = useMemo(() => {
  const map = {};
  appointments.forEach((a) => {
    const rawTime = a.appointment_time.slice(0, 5);
    const slotKey = getNearestSlot(rawTime);
    map[`${a.staff_id}-${slotKey}`] = a;
  });
  return map;
}, [appointments]);

  return (
    <div className="appt-page">
      {/* HEADER */}
      <div className="appt-header">
        <div>
          <h2>Appointments</h2>
          <p>Manage bookings, schedules, and walk-ins</p>
        </div>
        <div className="header-actions">
          <button className="btn-outline" onClick={() => openForm('walkin')}>
            <FaWalking /> Add Walk-in
          </button>
          <button className="btn-primary" onClick={() => openForm('booking')}>
            <FaUserPlus /> New Appointment
          </button>
        </div>
      </div>

      {error && <div className="appt-error-banner">{error}</div>}

      {/* DATE NAV + VIEW TOGGLE */}
      <div className="appt-toolbar">
        <div className="date-nav">
          <button className="nav-btn" onClick={() => changeDay(-1)}>
            <FaChevronLeft />
          </button>
          <div className="date-display">
            <FaCalendarAlt className="date-icon" />
            {formatDate(currentDate)}
          </div>
          <button className="nav-btn" onClick={() => changeDay(1)}>
            <FaChevronRight />
          </button>
          <button className="today-btn" onClick={() => setCurrentDate(new Date())}>
            Today
          </button>
        </div>

        <div className="view-toggle">
          <button
            className={view === 'schedule' ? 'active' : ''}
            onClick={() => setView('schedule')}
          >
            <FaThLarge /> Schedule
          </button>
          <button
            className={view === 'list' ? 'active' : ''}
            onClick={() => setView('list')}
          >
            <FaListUl /> List
          </button>
        </div>
      </div>

      {loading && <div className="appt-loading">Loading appointments...</div>}

      {/* SCHEDULE GRID VIEW */}
      {!loading && view === 'schedule' && (
        <div className="schedule-wrapper">
          <div
            className="schedule-grid"
            style={{ gridTemplateColumns: `90px repeat(${staffList.length || 1}, 1fr)` }}
          >
            <div className="schedule-corner"></div>
            {staffList.map((s) => (
              <div className="schedule-staff-head" key={s.id}>
                <FaUserCircle className="staff-avatar-icon" />
                {s.name}
              </div>
            ))}

           
{TIME_SLOTS.map((slot) => (
  <div className="schedule-row" key={slot} style={{ display: 'contents' }}>
    <div className="schedule-time-label">
      {slot} <span className="time-12h">({to12Hour(slot)})</span>
    </div>
    {staffList.map((s) => {
      const appt = appointmentsByStaffAndTime[`${s.id}-${slot}`];
      return (
        <div className="schedule-cell" key={`${s.id}-${slot}`}>
          {appt && (
            <div className={`appt-block ${STATUS_STYLES[appt.status]?.cls}`}>
              <strong>{appt.client_name}</strong>
              <span>{appt.service_name}</span>
            </div>
          )}
        </div>
      );
    })}
  </div>
))}
          </div>
        </div>
      )}

      {/* LIST VIEW */}
      {!loading && view === 'list' && (
        <div className="appt-list">
          {sortedAppointments.map((a) => (
            <div className="appt-card" key={a.id}>
              <div className="appt-time-col">
                <FaClock className="time-icon" />
                {a.appointment_time.slice(0, 5)} ({to12Hour(getCurrentTime())})
              </div>

              <div className="appt-info-col">
                <div className="appt-client-row">
                  <FaUserCircle className="client-icon" />
                  <strong>{a.client_name}</strong>
                  {a.client_phone && (
                    <span className="appt-phone">
                      <FaMobileAlt /> {a.client_phone}
                    </span>
                  )}
                </div>
                <div className="appt-meta-row">
                  <span>
                      {a.service_name}
                  </span>
                  <span>with {a.staff_name}</span>
                </div>
              </div>

              {/* // In List View — add a "Bill" button next to the status select */}
<div className="appt-status-col">
  <span className={`status-badge ${STATUS_STYLES[a.status]?.cls}`}>
    {STATUS_STYLES[a.status]?.icon}
    {STATUS_STYLES[a.status]?.label}
  </span>
  <select
    value={a.status}
    onChange={(e) => updateStatus(a.id, e.target.value)}
    className="status-select"
  >
    <option value="upcoming">Upcoming</option>
    <option value="progress">In Progress</option>
    <option value="completed">Completed</option>
    <option value="walkin">Walk-in</option>
    <option value="cancelled">Cancelled</option>
  </select>
  <button className="appt-bill-btn" onClick={() => goToBilling(a.id)}>
    <FaReceipt /> Bill
  </button>
</div>
            </div>
          ))}
          {sortedAppointments.length === 0 && (
            <div className="appt-empty">No appointments for this day</div>
          )}
        </div>
      )}

      {/* BOOKING MODAL */}
      {showBookingForm && (
        <div className="modal-overlay" onClick={() => setShowBookingForm(false)}>
          <div className="modal-box" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
            <h3>
  {formType === 'walkin' ? 'Add Walk-in Customer' : 'Book New Appointment'}
  {formType === 'walkin' && (
    <>
      <strong>&nbsp;[Time: {getCurrentTime()} ({to12Hour(getCurrentTime())})]</strong>
    </>
  )}
</h3>

              


              <button className="modal-close-btn" onClick={() => setShowBookingForm(false)}>
                <FaTimes />
              </button>
            </div>

            <form onSubmit={handleSubmit} className="modal-form">
              <label className="field-label">Client Name</label>
              <div className="input-group">
                <FaUserCircle className="input-icon" />
                <input
                  type="text"
                  placeholder="Client name"
                  value={form.client}
                  onChange={(e) => setForm({ ...form, client: e.target.value })}
                  required
                />
              </div>

              <label className="field-label">Phone Number</label>
              <div className="input-group">
                <FaMobileAlt className="input-icon" />
                <input
                  type="tel"
                  placeholder="Phone number"
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                />
              </div>

              <div className="modal-form-row">
                <div>
                  <label className="field-label">Service</label>
                  <select
                    value={form.serviceId}
                    onChange={(e) => setForm({ ...form, serviceId: e.target.value })}
                  >
                    <option value="">Select service</option>
                    {servicesList.map((s) => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label className="field-label">Staff</label>
                  <select
                    value={form.staffId}
                    onChange={(e) => setForm({ ...form, staffId: e.target.value })}
                  >
                    <option value="">Select staff</option>
                    {staffList.map((s) => (
                      <option key={s.id} value={s.id}>{s.name}</option>
                    ))}
                  </select>
                </div>
              </div>


              



              {formType === 'booking' && (
                <>
                  <label className="field-label">Time Slot</label>
                 <select
  value={form.time}
  onChange={(e) => setForm({ ...form, time: e.target.value })}
>
  {TIME_SLOTS.map((t) => (
    <option key={t} value={t}>
      {t} ({to12Hour(t)})
    </option>
  ))}
</select>
                </>
              )}

              <button type="submit" className="btn-primary full-width" disabled={saving}>
                {saving ? 'Saving...' : formType === 'walkin' ? 'Add Walk-in' : 'Confirm Booking'}
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}

export default Appointments;